import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { useApiQuery } from '@/hooks/use-api'

export type QuotaKey = 'members' | 'officialChannels' | 'personalChannels'
export type Quotas = Record<QuotaKey, number>

export interface BillingLimits {
  /** null = Không giới hạn / theo thoả thuận. */
  members: number | null
  officialChannels: number | null
  personalChannels: number | null
  activeContactsPerMonth: number | null
  aiRepliesPerDay: number | null
}

/** GET /billing/summary — gói hiện tại + số đang dùng của tổ chức. */
export interface BillingSummary {
  plan: string | null
  /** Gói có hiệu lực (mã trong bảng giá); null = gói thoả thuận riêng (nhãn tuỳ chỉnh). */
  effectivePlan: string | null
  /** Tên gói theo bảng giá. */
  planName: string | null
  /** Gói trả phí đã hết hạn → đang dùng như gói Miễn phí. */
  wasDowngraded: boolean
  expiresAt: string | null
  /** active | suspended | expired | unlimited */
  status: string
  /** Hạn mức hiện có (số đã mua hoặc số gồm sẵn của gói). */
  limits: BillingLimits
  /** Số lượng đang hiệu lực = đơn của kỳ hiện tại + phần mua thêm còn hạn. */
  purchased: Quotas | null
  /** Phần "Mua thêm" còn hiệu lực (đã cộng trong `purchased`); null = không có. Server cũ: không có. */
  addonPurchased?: Quotas | null
  /**
   * Gói trả phí còn hạn → chỉ nâng gói / mua thêm: không hạ gói, không giảm số lượng dưới `purchased`
   * ("mua rồi không trả lại" — server kiểm, 400 DOWNGRADE_NOT_ALLOWED / QUANTITY_DECREASE_NOT_ALLOWED).
   * Server cũ: không có (coi như false).
   */
  upgradeOnly?: boolean
  usage: {
    members: number
    channelsOfficial: number
    channelsPersonal: number
    contacts: number
    /** Khách có tin nhắn đến trong 30 ngày gần nhất. */
    activeContacts30d: number
    aiToday: number
    /** null = tổ chức chưa bật AI lần nào. */
    aiDailyLimit: number | null
  }
}

export function useBillingSummary() {
  return useApiQuery<BillingSummary>(['billing-summary'], '/billing/summary', undefined, { staleTime: 60_000 })
}

/** Một bậc giá phụ phí: đơn vị thứ k ≤ upTo (null = trở lên), VND / đơn vị / tháng. */
export interface PriceTier { upTo: number | null; unitPrice: number }

export interface PricingPlan {
  id: string
  name: string
  description: string
  /** VND / tháng. null = Liên hệ. */
  monthlyPrice: number | null
  included: Quotas
  max: Record<QuotaKey, number | null>
  activeContactsPerMonth: number | null
  aiRepliesPerDay: number | null
  /** AI hai chế độ — credit AI tặng mỗi tháng (server mới; không có → dùng aiRepliesPerDay). */
  aiCreditsPerMonth?: number | null
  /** Trần nhúng tài liệu miễn phí / ngày. */
  aiEmbedFreePerDay?: number | null
  /** Trần credit AI của eCDP / ngày (null = không trần). */
  aiPlatformDailyCap?: number | null
  /** Dòng mở đầu danh sách tính năng + tính năng của gói (do super-admin đặt; server cũ không có → dùng PLAN_META). */
  featuresIntro?: string
  features?: string[]
  purchasable: boolean
  highlight: boolean
  hidden: boolean
}

export interface DurationOption { days: number; discountPercent: number; badge: string }

/** GET /billing/plans — bảng giá do super-admin đặt (Platform → Bảng giá gói). */
export interface PricingConfig {
  version: 1
  currency: 'VND'
  plans: PricingPlan[]
  addons: Record<QuotaKey, PriceTier[]>
  durations: DurationOption[]
  roundTo: number
  defaultDays: number
  note: string
  /** Thuế suất VAT (%) cộng thêm khi thanh toán — giá niêm yết chưa gồm VAT (Q2). 0 = không chịu thuế. Server cũ: không có. */
  vatPercent?: number
}

export function useBillingPlans() {
  return useApiQuery<PricingConfig>(['billing-plans'], '/billing/plans', undefined, { staleTime: 5 * 60_000 })
}

export interface QuoteLine { key: QuotaKey; label: string; quantity: number; included: number; extra: number; monthly: number }

export interface QuotePromo {
  id: string
  code: string
  description: string
  discountType: 'percent' | 'fixed'
  discountValue: number
  /** VND giảm thực tế. */
  amount: number
  /** false = mã thay cho giảm giá thời hạn (không cộng dồn). */
  stacked: boolean
}

export interface QuoteVat { percent: number; preTax: number; amount: number }

/** POST /billing/quote — cùng công thức server dùng khi thanh toán. */
export interface Quote {
  planId: string
  planName: string
  days: number
  quantities: Quotas
  basePerMonth: number
  lines: QuoteLine[]
  perMonth: number
  subtotal: number
  discountPercent: number
  discount: number
  /** Mã ưu đãi đã áp (giảm sau giảm giá thời hạn, trước quy đổi). */
  promo: QuotePromo | null
  /** Lý do mã khách nhập không áp được (tiếng Việt). */
  promoError: string | null
  /** Ghi chú (không phải lỗi): mã hợp lệ nhưng không áp — vd không cộng dồn, giảm theo thời hạn lợi hơn. Vẫn đặt hàng được. */
  promoNote?: string | null
  /** Giá đơn trước khi trừ quy đổi gói cũ (đã trừ mã ưu đãi). */
  grossTotal: number
  /** Quy đổi thời hạn còn lại của gói đang dùng (khi đổi sang gói khác). */
  credit: { amount: number; applied: number; fromPlanId: string; fromPlanName: string; remainingDays: number } | null
  /** Ngày cộng thêm từ phần quy đổi còn dư (+ ngày còn lại chuyển sang kỳ mới — carriedDays). */
  extraDays: number
  /** Đổi số lượng khi kỳ hiện tại không có giá trị quy đổi (ngày tặng): số ngày còn lại chuyển sang kỳ mới (đã gồm trong extraDays). */
  carriedDays?: number
  totalDays: number
  /** Số tiền phải trả = trước thuế (đã trừ quy đổi) + VAT. Đơn cũ (không có `vat`): số đã trả. */
  total: number
  /** VAT cộng thêm (Q2): preTax = tạm tính chưa VAT, amount = tiền VAT. Đơn cũ: không có. */
  vat?: QuoteVat
  /** 'addon' = mua thêm cho kỳ hiện tại; 'plan' = mua / gia hạn / đổi gói; 'ai_credits' = mua credit AI. Đơn cũ: không có. */
  kind?: 'plan' | 'addon' | 'ai_credits'
  /** Đơn mua credit AI: gói đã chọn + số credit cộng khi thanh toán. */
  /** Đơn mua token AI: giá gói (đ), giá trị nạp (đ, giá bán ra), ≈ token theo model tổ chức lúc mua. Đơn cũ thiếu trường mới. */
  aiCredits?: {
    packId: string; credits: number; price?: number; label?: string
    valueVnd?: number; tokensApprox?: number | null; model?: string | null
    /** Số lượt AI của gói (đơn vị khách thấy). Đơn cũ: undefined. */
    turns?: number | null
  } | null
  /** Cách đơn áp vào gói đang dùng. */
  mode?: PurchaseMode
  /** Chỉ có với đơn mua thêm. */
  addon?: { from: Quotas; delta: Quotas; periodEnd: string; remainingDays: number } | null
}

/**
 * new = chưa có gói trả phí còn hạn · extend = gia hạn nối tiếp (cùng gói, cùng số lượng) ·
 * requantity = đổi số lượng cùng gói (quy đổi phần còn lại, kỳ mới từ hôm nay) · change_plan = đổi gói ·
 * addon = mua thêm cho kỳ hiện tại.
 */
export type PurchaseMode = 'new' | 'extend' | 'requantity' | 'change_plan' | 'addon'

export type QuoteRequest =
  | { planId: string; days: number; quantities: Quotas; promoCode?: string }
  /** "Mua thêm": chỉ số lượng mới (≥ số đang có) — server tự lấy gói, hạn, % giảm của kỳ hiện tại. */
  | { mode: 'addon'; quantities: Quotas; promoCode?: string }

export function useQuote(input: QuoteRequest | null) {
  return useQuery<Quote>({
    queryKey: ['billing-quote', input],
    enabled: !!input,
    queryFn: async () => {
      const { data } = await api.post<Quote>('/billing/quote', input)
      return data
    },
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
  })
}

export type BillingOrderStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'cancelled' | 'underpaid'

/** Tất cả qua EvoPay: card = 2C2P (thẻ, ví, QR) · bank = chuyển khoản VietQR · paypal = PayPal (USD) · sepay = Cổng thanh toán SePay. */
export type PaymentMethod = 'card' | 'bank' | 'paypal' | 'sepay' | 'tingee'

/** Form đã ký để POST sang trang thanh toán của cổng (Cổng SePay) — giữ ĐÚNG thứ tự trường. */
export interface CheckoutForm {
  action: string
  fields: [string, string][]
}

export interface PaymentMethodOption {
  id: PaymentMethod
  label: string
  /** PayPal: tỷ giá VND / 1 USD eCDP dùng để tính số USD thu. */
  vndPerUsd?: number
}

/** GET /billing/methods — phương thức thanh toán đang bật (theo cấu hình server), phần tử đầu là mặc định. */
export function useBillingMethods() {
  return useApiQuery<{ methods: PaymentMethodOption[]; /** false = chưa cấu hình hoá đơn điện tử (hoá đơn sẽ phát hành bổ sung). */ invoiceReady?: boolean }>(['billing-methods'], '/billing/methods', undefined, { staleTime: 10 * 60_000 })
}

/** Thông tin chuyển khoản VietQR (EvoPay) — nội dung chuyển khoản phải giữ nguyên để hệ thống tự xác nhận. */
export interface BankTransferInfo {
  bankCode: string
  accountNumber: string
  accountName: string
  amount: number
  content: string
  qrImageUrl: string
}

export interface BillingOrder {
  id: string
  planId: string
  /** d<số ngày>; "addon" = mua thêm cho kỳ hiện tại; đơn cũ month | year. */
  cycle: string
  days: number
  /** Báo giá lúc đặt (null với đơn cũ). */
  quote: Quote | null
  amount: number
  currency: string
  /** evopay_card / evopay_bank / evopay_paypal / evopay_sepay / evopay_tingee = qua EvoPay. Đơn cũ: sepay_pg, sepay, zalopay (cổng đã gỡ), manual / bank_transfer (Platform). */
  provider: string
  appTransId: string | null
  zpTransId: string | null
  /** EvoPay: mã thanh toán (nội dung chuyển khoản / mã 2C2P). */
  paymentCode: string | null
  /** EvoPay: id thanh toán phía EvoPay. */
  providerTxnId: string | null
  expiresAt: string | null
  status: BillingOrderStatus
  orderUrl: string | null
  /** Đơn chuyển khoản EvoPay còn chờ: QR + thông tin chuyển khoản (null với đơn khác / đã chốt). */
  bankTransfer?: BankTransferInfo | null
  /** Đơn Cổng SePay còn chờ: form để "Tiếp tục thanh toán" (null với đơn khác / đã chốt). */
  checkoutForm?: CheckoutForm | null
  paidAt: string | null
  periodStart: string | null
  periodEnd: string | null
  createdAt: string
  /** Hoá đơn điện tử SePay (không có ở server cũ). */
  invoice?: OrderInvoice
}

// ── Hoá đơn điện tử ──────────────────────────────────────────────────

export type BuyerType = 'company' | 'individual'
export type InvoiceStatus = 'none' | 'pending' | 'issued' | 'failed' | 'cancelled'

/** Thông tin xuất hoá đơn (người mua). */
export interface InvoiceInfo {
  buyerType: BuyerType
  companyName: string | null
  taxCode: string | null
  buyerName: string | null
  nationalId: string | null
  address: string | null
  emails: string[]
  phone: string | null
  bankAccount: string | null
  bankName: string | null
}

export interface InvoiceProfile extends InvoiceInfo {
  id: string
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface OrderInvoice {
  status: InvoiceStatus
  buyer: string | null
  taxCode: string | null
  emails: string[]
  invoiceNo: string | null
  pdfUrl: string | null
  hasXml: boolean
  issuedAt: string | null
  error: string | null
}

/** GET /billing/invoice-profiles — danh bạ thông tin xuất hoá đơn (owner/admin). */
export function useInvoiceProfiles(enabled = true) {
  return useQuery<InvoiceProfile[]>({
    queryKey: ['billing-invoice-profiles'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ profiles: InvoiceProfile[] }>('/billing/invoice-profiles')
      return data.profiles
    },
    staleTime: 60_000,
  })
}

export function useSaveInvoiceProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: InvoiceInfo & { id?: string; isDefault?: boolean }) => {
      const { data } = id
        ? await api.put<InvoiceProfile>(`/billing/invoice-profiles/${id}`, body)
        : await api.post<InvoiceProfile>('/billing/invoice-profiles', body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-invoice-profiles'] }),
  })
}

export function useDeleteInvoiceProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/billing/invoice-profiles/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-invoice-profiles'] }),
  })
}

/** POST /billing/orders/:id/invoice — yêu cầu xuất hoá đơn cho đơn đã thanh toán. */
export function useRequestInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ orderId, invoice }: { orderId: string; invoice: Record<string, unknown> }) => {
      const { data } = await api.post(`/billing/orders/${orderId}/invoice`, { invoice })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing-orders'] })
      qc.invalidateQueries({ queryKey: ['billing-invoice-profiles'] })
    },
  })
}

/**
 * Tải file hoá đơn qua server: file → lưu; server trả {url} (link SePay) → mở tab mới.
 */
export async function downloadOrderInvoice(orderId: string, type: 'pdf' | 'xml' = 'pdf'): Promise<void> {
  const res = await api.get(`/billing/orders/${orderId}/invoice/file`, { params: { type }, responseType: 'blob' })
  const blob = res.data as Blob
  if (blob.type.includes('json')) {
    const { url } = JSON.parse(await blob.text()) as { url?: string }
    if (url) window.open(url, '_blank', 'noopener')
    return
  }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `hoa-don-${orderId.slice(0, 8)}.${type}`
  a.click()
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
}

export function useBillingOrders(opts: { enabled?: boolean; staleTime?: number } = {}) {
  return useApiQuery<{ orders: BillingOrder[] }>(['billing-orders'], '/billing/orders', undefined, {
    ...(opts.enabled !== undefined ? { enabled: opts.enabled } : {}),
    ...(opts.staleTime !== undefined ? { staleTime: opts.staleTime } : {}),
  })
}

/**
 * Theo dõi một đơn: hỏi lại mỗi 3s tới khi hết pending. Server tự đối soát với cổng
 * thanh toán khi webhook chưa tới (giới hạn 10 giây / lần ở server).
 */
export function useBillingOrder(idOrAppTransId: string | null, opts: { poll?: boolean } = {}) {
  const poll = opts.poll ?? true
  return useQuery<BillingOrder>({
    queryKey: ['billing-order', idOrAppTransId],
    enabled: !!idOrAppTransId,
    queryFn: async () => {
      const { data } = await api.get<BillingOrder>(`/billing/orders/${encodeURIComponent(idOrAppTransId!)}`)
      return data
    },
    refetchInterval: (q) => (!poll || (q.state.data && q.state.data.status !== 'pending') ? false : 3_000),
    retry: 2,
  })
}

export type CheckoutResult =
  /** EvoPay 2C2P / PayPal: chuyển sang trang thanh toán của cổng. PayPal kèm số USD thu thực tế. */
  | { method: 'card' | 'paypal' | 'tingee'; orderId: string; invoiceNumber: string; expiresAt: string; redirectUrl: string; charge?: { currency: 'USD'; amount: number; vndPerUsd: number } }
  /** EvoPay chuyển khoản: hiện QR ở /billing?payment=bank&order=<id>. */
  | { method: 'bank'; orderId: string; invoiceNumber: string; expiresAt: string; bankTransfer: BankTransferInfo }
  /** EvoPay Cổng SePay: POST form đã ký sang trang SePay. */
  | { method: 'sepay'; orderId: string; invoiceNumber: string; expiresAt: string; form: CheckoutForm }
  /** Giá trị gói cũ / mã ưu đãi trừ hết giá đơn → kích hoạt ngay, không qua cổng thanh toán. */
  | { method: 'credit'; orderId: string; status: 'paid' }

export function useCheckout() {
  const qc = useQueryClient()
  return useMutation({
    // Đơn mới → thanh "đơn đang chờ" toàn cục / lịch sử phải thấy ngay.
    onSettled: () => { qc.invalidateQueries({ queryKey: ['billing-orders'] }) },
    mutationFn: async (body: QuoteRequest & {
      method?: PaymentMethod
      /** Mã ưu đãi — server kiểm lại, mã không còn hợp lệ → 400. */
      promoCode?: string
      /** Thông tin xuất hoá đơn (pages/billing/invoice-info.buildInvoiceBody); null = không xuất. */
      invoice?: Record<string, unknown> | null
      /** Trang nhận kết quả từ cổng thanh toán (/billing, /billing/upgrade?… hoặc /billing/addon?…) — server kiểm lại. */
      returnTo?: string
    }) => {
      const { data } = await api.post<CheckoutResult>('/billing/checkout', body)
      return data
    },
  })
}

/** Huỷ đơn EvoPay còn chờ. Khách vừa trả kịp → server trả đơn `paid` (không huỷ). */
export function useCancelOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data } = await api.post<BillingOrder>(`/billing/orders/${encodeURIComponent(orderId)}/cancel`)
      return data
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['billing-orders'] })
      qc.invalidateQueries({ queryKey: ['billing-order'] })
      qc.invalidateQueries({ queryKey: ['billing-summary'] })
    },
  })
}

/** "Tạo mã mới" / "Thanh toán lại": dựng lại đơn chưa trả từ báo giá đã lưu, trả như checkout (card | paypal | bank). */
export function useRenewOrder() {
  return useMutation({
    mutationFn: async (vars: { orderId: string; returnTo?: string }) => {
      const { data } = await api.post<Exclude<CheckoutResult, { method: 'credit' }>>(
        `/billing/orders/${encodeURIComponent(vars.orderId)}/renew`,
        { returnTo: vars.returnTo },
      )
      return data
    },
  })
}

/** Nhân viên thường nhờ chủ sở hữu / quản trị viên thanh toán gói đang chọn (không tạo đơn). */
export function useRequestPayment() {
  return useMutation({
    mutationFn: async (body: { planId: string; days: number }) => {
      const { data } = await api.post<{ notified: number }>('/billing/payment-requests', body)
      return data
    },
  })
}
